import { type HttpClient, query, segment } from '../http';

import {
  type CataloguePage,
  type DeletionImpact,
  type ListCatalogueQuery,
  type GameMergeResult,
} from './catalogue.types';

/**
 * The catalogue, and the ways a sheet can leave it.
 *
 * Warning: administration routes. This is the only surface that reaches a
 * private sheet nobody ever submitted — the moderation queue reads what was
 * offered, and a sheet typed in and left alone appears nowhere else.
 */
export function createCatalogueApi(http: HttpClient) {
  return {
    /** One page, and every chip's count under the same search. */
    list: (criteria: ListCatalogueQuery = {}) =>
      http.get<CataloguePage>(
        `/admin/games${query({
          search: criteria.search,
          filter: criteria.filter,
          limit: criteria.limit,
          offset: criteria.offset,
        })}`,
      ),

    /**
     * What deleting this sheet would actually do.
     *
     * ⚠️ Read **before** offering the button. `canDelete` is what
     * `onDelete: Restrict` will allow, not what the screen prefers — without
     * it an administrator meets the refusal as a 500.
     */
    impact: (id: string) =>
      http.get<DeletionImpact>(`/admin/games/${segment(id)}/impact`),

    /**
     * Takes a sheet out of the public catalogue.
     *
     * ⚠️ The lightest way out: everybody who already has it keeps it, with
     * their purchase price and their notes.
     */
    unpublish: (id: string) =>
      http.post<void>(`/admin/games/${segment(id)}/unpublish`),

    /**
     * Absorbs one sheet into another.
     *
     * ⚠️ The way out that loses nothing, and the one to reach for first: a
     * sheet somebody wants to delete is usually a duplicate. Owners keep their
     * purchase price and their notes; only the game they point at changes.
     *
     * @param id The sheet that disappears.
     * @param intoId The sheet that stays.
     * @returns What moved, and the few rows a merge has to drop.
     */
    merge: (id: string, intoId: string) =>
      http.post<GameMergeResult>(`/admin/games/${segment(id)}/merge`, { intoId }),

    /** Puts a sheet aside. Reversible, and nothing is erased. */
    trash: (id: string) => http.post<void>(`/admin/games/${segment(id)}/trash`),

    restore: (id: string) =>
      http.post<void>(`/admin/games/${segment(id)}/restore`),

    /**
     * Erases a sheet for good.
     *
     * ⚠️ The title is retyped, not confirmed. Copying it takes three seconds
     * and forces somebody to read which sheet they are erasing — the only
     * safeguard that survives fatigue.
     *
     * ⚠️ A POST, because it carries that title. A DELETE with a body is
     * dropped by enough proxies to fail somewhere nobody can reproduce.
     */
    remove: (id: string, title: string) =>
      http.post<DeletionImpact>(`/admin/games/${segment(id)}/delete`, {
        title,
      }),
  };
}
